import { useCallback } from "react";
import { api } from "../../services/api/api";
import { useWorkorderContext } from "../../context/WorkorderContext";
import toast from "react-hot-toast";
import { t } from "i18next";

/**
 * Hook pour gérer les hoses dans un workorder
 * @param workorderId - ID du workorder
 * @returns Fonctions pour manipuler les hoses
 */
export function useWorkorderHose(workorderId: string) {
  const {updateHose} = useWorkorderContext();
  const workorderContext = useWorkorderContext();

  /**
   * Ajoute un hose au workorder
   * @param hoseData - Données du hose à ajouter
   * @returns Le hose créé
   */
  const addHose = useCallback(
    async (hoseData: Partial<EasyTech.Hose>) => {
      try {
        const hoseWithWorkorder = {
          name: hoseData.name,
          description: hoseData.description,
          barCode: hoseData.barCode || "",
          qrCode: hoseData.qrCode,
          workOrder: { id: workorderId },
          machine: hoseData.machine,
        };

        const response = await api.post(
          "users/hoses",
          hoseWithWorkorder
        );

        if (response) {
          if (workorderContext && workorderContext.addHose) {
            workorderContext.addHose(response as EasyTech.WorkorderHose);
          }
        }

        return response;
      } catch (error) {
        toast.error(t("workorder:hoses:error:addHose"));
        throw error;
      }
    },
    [workorderId, workorderContext]
  );

  /**
   * Met à jour un hose existant
   * @param hoseId - ID du hose à mettre à jour
   * @param hoseData - Nouvelles données du hose
   * @returns Le hose mis à jour
   */
  const editHose = useCallback(
    async (hoseId: string, hoseData: Partial<EasyTech.Hose>) => {
      try {
        const response = await api.put(
          `users/hoses/${hoseId}`,
          hoseData
        );

        updateHose(hoseId, response as EasyTech.WorkorderHose);

        return response;
      } catch (error) {
        toast.error(t("workorder:hoses:error:editHose"));
        throw error;
      }
    },
    [updateHose]
  );

  /**
   * Supprime un hose du workorder
   * @param hoseId - ID du hose à supprimer
   * @returns true si la suppression a réussi
   */
  const removeHose = useCallback(
    async (hoseId: string) => {
      try {
        await api.delete(`users/hoses/${hoseId}`);
        
        if (workorderContext && workorderContext.removeHose) {
          workorderContext.removeHose(hoseId);
        }
        
        return true;
      } catch (error) {
        toast.error(t("workorder:hoses:error:removeHose"));
        throw error;
      }
    },
    [workorderContext]
  );

  return {
    addHose,
    editHose,
    removeHose,
  };
}